import {
  CLIENT_TOOL_GROUPS,
  CLIENT_TOOLS,
  OVERVIEW_KEY,
  SESSIONS_KEY,
  SKILLS_SUBGROUPS,
  clientToolOf,
  type ClientToolGroup,
  type SkillsSubgroup,
} from '@cc/schema';

/**
 * E57 · the client page's A5 navigation, as data (registry decisions `client-page-is-home`, `groups`, `key-map`,
 * `hidden-edge-cases`; Q-e57g1-1b A; Q-e57g1-6 A).
 *
 * Row 1: Overview · Sessions & notes · Plan · Track · Formulation · Skills & exercises · Safety. Row 2: the chosen group's
 * tools — the SAME pills in the SAME canonical order for every client (My tools decides which exist); "in use" only
 * styles a pill. The URL keeps `/clients/show/:id?tab=<tool key>` (the twelve shipped keys unchanged); the group is
 * derived from the tool. Overview has two pills: the digest (default) and `&view=details` (the old Overview). A group
 * whose tools are all hidden opens as `?group=<g>` with an empty line.
 *
 * D-nav-fixes-2026-10-19 (founder Q-nav-3 B, Q-nav-5 B): the two rows stay, and row 2 WRAPS onto more lines instead of
 * folding tools behind "⋯" — nothing is hidden at any width, in either language. Skills & exercises' row carries four small
 * labels between its tools (`rowTwoClusters`). Q-nav-9 (founder, 2026-10-19): the rows carry NAMES only — no record count, no
 * "new" (they ate space and data); "in use" still dims an unused tool and "hidden by you" still marks a hidden one in use.
 */
export type RowOneKey = typeof OVERVIEW_KEY | typeof SESSIONS_KEY | ClientToolGroup;
export const ROW_ONE: readonly RowOneKey[] = [OVERVIEW_KEY, SESSIONS_KEY, ...CLIENT_TOOL_GROUPS];

export type ClientSection =
  | { kind: 'overview'; view: 'digest' | 'details' }
  | { kind: 'sessions' }
  | { kind: 'tool'; tool: string; group: ClientToolGroup }
  | { kind: 'group'; group: ClientToolGroup };

/** The section a URL names; an unknown `tab` falls back to the Overview digest. */
export const sectionOf = (params: URLSearchParams): ClientSection => {
  const tab = params.get('tab');
  if (tab === SESSIONS_KEY) return { kind: 'sessions' };
  const tool = clientToolOf(tab);
  if (tool) return { kind: 'tool', tool: tool.key, group: tool.group };
  const group = params.get('group');
  if ((CLIENT_TOOL_GROUPS as readonly string[]).includes(group ?? '')) {
    return { kind: 'group', group: group as ClientToolGroup };
  }
  return { kind: 'overview', view: params.get('view') === 'details' ? 'details' : 'digest' };
};

export const rowOneOf = (section: ClientSection): RowOneKey =>
  section.kind === 'overview'
    ? OVERVIEW_KEY
    : section.kind === 'sessions'
    ? SESSIONS_KEY
    : section.group;

/** The URL params of a section (other params — none today — are dropped: the section is the page's whole state). */
export const paramsOf = (section: ClientSection): URLSearchParams => {
  const p = new URLSearchParams();
  switch (section.kind) {
    case 'overview':
      if (section.view === 'details') {
        p.set('tab', OVERVIEW_KEY);
        p.set('view', 'details');
      }
      break;
    case 'sessions':
      p.set('tab', SESSIONS_KEY);
      break;
    case 'tool':
      p.set('tab', section.tool);
      break;
    case 'group':
      p.set('group', section.group);
      break;
  }
  return p;
};

export type RowTool = {
  tool: string;
  group: ClientToolGroup;
  testId: string;
  inUse: boolean;
  hiddenByMe: boolean;
  /** Opened by a deep link to a tool hidden and not in use — its pill shows while it is open. */
  deepLinked?: boolean;
};

export type NavItem = {
  tool: string;
  group: string;
  inUse: boolean;
  hiddenByMe: boolean;
};

/**
 * Row 2 of a group. From the server's ClientToolNavigation when it has answered; until then (or when it failed) the
 * catalogue minus the viewer's hidden set, every pill plain — never an empty row while loading. A deep link to a tool
 * that is not in the row (hidden, not in use) adds its pill in its canonical place (the board: "a deep link ?tab=<hidden
 * tool> … opens the section and shows its pill").
 */
export const rowTwoOf = (
  group: ClientToolGroup,
  nav: readonly NavItem[] | null,
  hiddenTools: readonly string[],
  openTool: string | null,
): RowTool[] => {
  const hidden = new Set(hiddenTools);
  const byKey = new Map((nav ?? []).map((n) => [n.tool, n]));
  const out: RowTool[] = [];
  for (const def of CLIENT_TOOLS) {
    if (def.group !== group) continue;
    const n = byKey.get(def.key);
    if (nav) {
      if (n) {
        out.push({
          tool: def.key,
          group,
          testId: def.testId,
          inUse: n.inUse,
          hiddenByMe: n.hiddenByMe,
        });
      } else if (def.key === openTool) {
        out.push({ tool: def.key, group, testId: def.testId, inUse: false, hiddenByMe: true, deepLinked: true });
      }
      continue;
    }
    if (!hidden.has(def.key) || def.key === openTool) {
      out.push({
        tool: def.key,
        group,
        testId: def.testId,
        // unknown yet: plain (never dimmed on a guess)
        inUse: true,
        hiddenByMe: hidden.has(def.key),
        deepLinked: hidden.has(def.key),
      });
    }
  }
  return out;
};

/** A run of row-2 pills under one small label (Skills & exercises only; `subgroup` null = no label). */
export type RowCluster = { subgroup: SkillsSubgroup | null; tools: RowTool[] };

/**
 * Row 2 cut into its labelled runs (Q-nav-5 B): Skills & exercises by the catalogue's `subgroup` — Cognitive · Behavioral ·
 * Values & motivation · Mindfulness, in that order, a Skills tool without a subgroup after them; every other group is one
 * run with no label. An empty run is not drawn (its label neither).
 */
export const rowTwoClusters = (group: ClientToolGroup, row: readonly RowTool[]): RowCluster[] => {
  if (group !== 'skills') return row.length ? [{ subgroup: null, tools: [...row] }] : [];
  const subgroupOf = (tool: string): SkillsSubgroup | null => clientToolOf(tool)?.subgroup ?? null;
  const out: RowCluster[] = SKILLS_SUBGROUPS.map((sg) => ({ subgroup: sg, tools: row.filter((r) => subgroupOf(r.tool) === sg) }));
  out.push({ subgroup: null, tools: row.filter((r) => subgroupOf(r.tool) === null) });
  return out.filter((c) => c.tools.length > 0);
};

/** Where a click on a row-1 group goes: its first pill, or the group's empty line when every tool of it is hidden. */
export const firstSectionOfGroup = (group: ClientToolGroup, row: readonly RowTool[]): ClientSection =>
  row.length ? { kind: 'tool', tool: row[0].tool, group } : { kind: 'group', group };

/** Tabs that word the refusal themselves and keep the practice's own actions (record an external result, assign, take one in session). */
const SAYS_NOT_SHARED_ITSELF: ReadonlySet<string> = new Set(['assessments']);

/**
 * P020 (E57 B5, F-203): a client who does not share view — the section of a tool of her OWN work (catalogue `ownWork`)
 * says so in one line instead of an error banner (the reads refuse) or an empty list that reads "has none". The practice's
 * own records (homework, worksheets, conceptualizations) open as always. `workShared` unknown (still loading) changes nothing.
 */
export const sectionSaysNotShared = (tool: string, workShared: boolean | null | undefined): boolean =>
  workShared === false && !!clientToolOf(tool)?.ownWork && !SAYS_NOT_SHARED_ITSELF.has(tool);
